import type { ColumnFiltersState, SortingState } from '@tanstack/react-table'
import dayjs from 'dayjs'
import { useMemo } from 'react'

import type { FeedbackSortBy, FeedbackType, GetFeedbackParams } from '@/services/api/api.types'

import type { DateRangeFilter } from './columns'
import { FEEDBACK_COLUMN } from './columns'

type FeedbackFilters = Omit<GetFeedbackParams, 'page' | 'pageSize'>

const sortByColumn: Record<string, FeedbackSortBy> = {
  [FEEDBACK_COLUMN.date]: 'date',
  [FEEDBACK_COLUMN.module]: 'content_module',
  [FEEDBACK_COLUMN.rating]: 'feedback_type',
}

/** Maps the table's filters and sort onto the feedback API's parameters. */
export function getFeedbackFilters(columnFilters: ColumnFiltersState, sorting: SortingState): FeedbackFilters {
  const filters: FeedbackFilters = {}
  const valueOf = <T>(columnId: string) => columnFilters.find(f => f.id === columnId)?.value as T | undefined

  const topicIds = valueOf<string[]>(FEEDBACK_COLUMN.topics)
  if (topicIds?.length) filters.topicIds = topicIds

  const contentModuleIds = valueOf<string[]>(FEEDBACK_COLUMN.module)
  if (contentModuleIds?.length) filters.contentModuleIds = contentModuleIds

  const feedbackType = valueOf<FeedbackType>(FEEDBACK_COLUMN.rating)
  if (feedbackType) filters.feedbackType = feedbackType

  const tags = valueOf<string[]>(FEEDBACK_COLUMN.tags)
  if (tags?.length) filters.tags = tags

  // Both ends arrive as a date-only string, so both parse to local midnight. The backend
  // filters created_at with $lte and documents end_date as inclusive, so the end needs the
  // end of that day or same-day feedback after midnight is dropped.
  const dateRange = valueOf<DateRangeFilter>(FEEDBACK_COLUMN.date)
  if (dateRange?.[0]) filters.startDate = dayjs(dateRange[0]).toISOString()
  if (dateRange?.[1]) filters.endDate = dayjs(dateRange[1]).endOf('day').toISOString()

  const comments = valueOf<string>(FEEDBACK_COLUMN.comment)
  if (comments) filters.comments = comments

  const sortBy = sorting[0] && sortByColumn[sorting[0].id]
  if (sortBy) {
    filters.sortBy = sortBy
    filters.sortOrder = sorting[0].desc ? 'desc' : 'asc'
  }

  return filters
}

export function useFeedbackFilters(columnFilters: ColumnFiltersState, sorting: SortingState): FeedbackFilters {
  return useMemo(() => getFeedbackFilters(columnFilters, sorting), [columnFilters, sorting])
}
